import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import {
  EmailSentHistoryFilters,
  EmailSentHistoryResponse,
  IEmailSentHistory,
} from '../../types/emailSentHistory/emailSentHistory.types';

const SERVER_URL = API_CONFIG.SERVER_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Get email sent history for a specific campaign
export const getEmailSentHistory = async (
  campaignId: string,
  filters?: EmailSentHistoryFilters
): Promise<EmailSentHistoryResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.dateRange?.start)
      params.append('startDate', filters.dateRange.start);
    if (filters?.dateRange?.end)
      params.append('endDate', filters.dateRange.end);
    if (filters?.page !== undefined)
      params.append('page', filters.page.toString());
    if (filters?.limit !== undefined)
      params.append('limit', filters.limit.toString());

    const response = await api.get(
      `/email-sent-history/${campaignId}?${params}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching email sent history:', error);
    throw error;
  }
};

// Get draft emails for a specific campaign
export const getDraftEmails = async (
  campaignId: string,
  filters?: EmailSentHistoryFilters
): Promise<EmailSentHistoryResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.dateRange?.start)
      params.append('startDate', filters.dateRange.start);
    if (filters?.dateRange?.end)
      params.append('endDate', filters.dateRange.end);
    if (filters?.page !== undefined)
      params.append('page', filters.page.toString());
    if (filters?.limit !== undefined)
      params.append('limit', filters.limit.toString());

    const response = await api.get(
      `/email-sent-history/draft/${campaignId}?${params}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching draft emails:', error);
    throw error;
  }
};

// Send individual email
export const sendEmail = async (
  emailId: string
): Promise<{ message: string }> => {
  try {
    const response = await api.post(`/email-sent-history/send/${emailId}`);
    return response.data;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

// Send all campaign emails
export const sendAllEmail = async (
  campaignId: string
): Promise<{ message: string }> => {
  try {
    const response = await api.post(
      `/email-sent-history/send-all/${campaignId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error sending all emails:', error);
    throw error;
  }
};

// Send test email
export const sendTestEmail = async (
  senderAccountId: string,
  emailTemplateId: string,
  contactId: string,
  emailAddress: string[]
): Promise<{ message: string }> => {
  try {
    const response = await api.post('/email-sent-history/test-send', {
      senderAccountId,
      emailTemplateId,
      contactId,
      emails: emailAddress,
    });
    return response.data;
  } catch (error) {
    console.error('Error sending test email:', error);
    throw error;
  }
};

// Export draft emails to CSV
export const exportDraftEmailsCSV = async (
  campaignId: string,
  startDate?: string,
  endDate?: string
): Promise<Blob> => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(
      `/email-sent-history/csv/draft/${campaignId}?${params}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error exporting draft emails:', error);
    throw error;
  }
};

// Export sent emails to CSV
export const exportSentEmailsCSV = async (
  campaignId: string,
  startDate?: string,
  endDate?: string
): Promise<Blob> => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(
      `/email-sent-history/csv/sent/${campaignId}?${params}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error exporting sent emails:', error);
    throw error;
  }
};

// Get email view details
export const getEmailView = async (
  emailId: string
): Promise<{ message: string; data: IEmailSentHistory }> => {
  try {
    const response = await api.get(`/email-sent-history/view/${emailId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching email view:', error);
    throw error;
  }
};

export default api;
